import { Button } from "@rosette/web_components/src/ui/Button.js";
import type { MutableRefObject } from "react";
import React from "react";
import {
    useArePlayersConnected,
    useGameClientControls, useGameController,
} from "@/components/game/GameUIContext.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { ReactionIcon } from "@/components/icon/ReactionIcon.js";
import { ReactionType } from "@rosette/ts/src/business/game/ReactionType.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";
import { GameMode } from "@rosette/ts/src/business/game/GameMode.js";
import { Path } from "@rosette/ts/src/util/Path.js";
import { ConfirmButton } from "@/components/game/menu/ConfirmButton.js";
import { MENU_BUTTON, MenuOptions } from "@/components/game/menu/MenuLayout.js";
import { Card } from "@rosette/web_components/src/ui/Card.js";


interface AbortGameMenuProps {
    contentRef: MutableRefObject<HTMLDivElement | null>;
    leaveTitle: string;
    leaveMessage: string;
    leaveButtonText: string;
    hasGameEnded: boolean;
    isSpectator: boolean;
    closeMenu: () => void;
}


export function AbortGameMenu({
    contentRef, leaveTitle, leaveMessage, leaveButtonText, hasGameEnded, isSpectator, closeMenu,
}: AbortGameMenuProps) {
    const clientControls = useGameClientControls();
    const controller = useGameController();
    const game = useRune(controller.getGame());
    const lobbyStatus = useRune(controller.getLobbyStatus());
    const players = useRune(controller.getPlayers());
    const { p1Connected, p2Connected } = useArePlayersConnected(lobbyStatus, players);
    const hasPlayerDisconnected = (!p1Connected || !p2Connected);

    const yourPlayer = players.getYourPlayer();
    const hasPlayedAnyMoves = (
        yourPlayer && (game.isFinished() || game.hasPlayerMadeAnyMoves(yourPlayer))
    );
    const mode = lobbyStatus.getGameMode();
    const showResign = (
        !isSpectator && !hasGameEnded && hasPlayedAnyMoves
        && (mode === GameMode.ONLINE || mode === GameMode.RANKED)
    );
    const showLeaveGame = (
        (mode !== GameMode.ONLINE && mode !== GameMode.RANKED)
        || isSpectator || hasGameEnded || !hasPlayedAnyMoves || hasPlayerDisconnected
    );
    const showSetupNewGame = (
        hasGameEnded || mode !== GameMode.ONLINE
    );

    return (
        <MenuOptions contentRef={contentRef}>
            <Card className="max-w-[24em] px-6 py-3 md:py-3">
                <h2 className="w-full m-0 text-center">
                    {leaveTitle}
                </h2>
                <p className="w-full m-0 text-center text-xl">
                    {leaveMessage}
                </p>
            </Card>

            {showResign && (
                <ConfirmButton
                    onSubmit={() => clientControls.resignGame()}>
                    <ReactionIcon
                        className={cn(
                            "align-middle mr-[0.25em] drop-shadow-md",
                            "[&_img]:w-full [&_img]:max-w-[1.25em] [&_img]:max-h-[1.25em]",
                        )}
                        reaction={ReactionType.RESIGN} />

                    Resign
                </ConfirmButton>
            )}

            {showLeaveGame && (
                <ConfirmButton
                    tone="danger"
                    onSubmit={() => clientControls.abortGame()}
                    skipConfirm={hasGameEnded}>

                    {leaveButtonText}
                </ConfirmButton>
            )}

            {showSetupNewGame && (
                <ConfirmButton
                    onSubmit={() => clientControls.abortGame(new Path(
                        "/game",
                        { tab: "rules" },
                    ))}
                    skipConfirm={hasGameEnded}>

                    Set up a new game
                </ConfirmButton>
            )}

            <Button
                size="lg"
                className={MENU_BUTTON}
                onClick={closeMenu}>
                Cancel
            </Button>
        </MenuOptions>
    );
}
